import { useEffect, useState } from "react";

import { callAuthenticatedWordinariusFunction } from "./platform/firebaseFunctions";

export type GuidedTrialPhase =
  | "practice"
  | "vocabulary_listen_repeat"
  | "vocabulary_unscramble"
  | "vocabulary_listen_write"
  | "missions"
  | "mixed_missions"
  | "complete";

export interface GuidedTrialState {
  phase: GuidedTrialPhase;
  started: boolean;
  status: "active" | "completed";
  updatedAt: string;
}

const LEGACY_STORAGE_KEY = "wordinarius_echo.web.guided_trial.v1";
const ACCOUNT_STORAGE_PREFIX = "wordinarius_echo.web.guided_trial.v2";

export const INITIAL_GUIDED_TRIAL_STATE: GuidedTrialState = {
  phase: "practice",
  started: false,
  status: "active",
  updatedAt: "",
};

const NEXT_PHASE: Record<Exclude<GuidedTrialPhase, "complete">, GuidedTrialPhase> = {
  practice: "vocabulary_listen_repeat",
  vocabulary_listen_repeat: "vocabulary_unscramble",
  vocabulary_unscramble: "vocabulary_listen_write",
  vocabulary_listen_write: "missions",
  missions: "mixed_missions",
  mixed_missions: "complete",
};

export function isGuidedTrialState(value: unknown): value is GuidedTrialState {
  if (!value || typeof value !== "object") {
    return false;
  }
  const candidate = value as Partial<GuidedTrialState>;
  return (candidate.status === "active" || candidate.status === "completed")
    && typeof candidate.started === "boolean"
    && Boolean(candidate.phase && (
      candidate.phase === "complete" || Object.hasOwn(NEXT_PHASE, candidate.phase)
    ));
}

export function startGuidedTrialPhase(state: GuidedTrialState): GuidedTrialState {
  if (state.status === "completed" || state.phase === "complete") {
    return state;
  }
  return { ...state, started: true, updatedAt: new Date().toISOString() };
}

export function completeGuidedTrialPhase(state: GuidedTrialState): GuidedTrialState {
  if (state.status === "completed" || state.phase === "complete") {
    return state;
  }
  return {
    ...state,
    phase: NEXT_PHASE[state.phase],
    started: false,
    updatedAt: new Date().toISOString(),
  };
}

export function finishGuidedTrial(state: GuidedTrialState): GuidedTrialState {
  if (state.phase !== "complete") {
    return state;
  }
  return {
    ...state,
    status: "completed",
    started: true,
    updatedAt: new Date().toISOString(),
  };
}

function accountStorageKey(accountId: string): string {
  return `${ACCOUNT_STORAGE_PREFIX}.${encodeURIComponent(accountId)}`;
}

function readStoredState(storageKey: string): GuidedTrialState | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    return isGuidedTrialState(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function persistStoredState(storageKey: string, state: GuidedTrialState): void {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(state));
  } catch {
    // The active browser session can continue when managed storage is unavailable.
  }
}

interface LearnerOnboardingResponse {
  completed: boolean;
  completedAt: string | null;
}

export function useGuidedTrial(options: {
  accountId: string | null;
  enabled: boolean;
  syncRemote: boolean;
}) {
  const [state, setState] = useState<GuidedTrialState>({ ...INITIAL_GUIDED_TRIAL_STATE });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    if (!options.enabled || !options.accountId) {
      setReady(false);
      return;
    }

    setReady(false);
    const scopedKey = accountStorageKey(options.accountId);
    const scopedState = readStoredState(scopedKey);
    const legacyState = readStoredState(LEGACY_STORAGE_KEY);
    const localState = scopedState || legacyState || { ...INITIAL_GUIDED_TRIAL_STATE };

    const hydrate = async () => {
      if (!options.syncRemote) {
        if (active) {
          setState(localState);
          setReady(true);
        }
        return;
      }
      try {
        const remote = await callAuthenticatedWordinariusFunction<LearnerOnboardingResponse>(
          "getLearnerOnboardingState",
          {},
        );
        if (!active) return;
        if (remote.completed) {
          const completed: GuidedTrialState = {
            phase: "complete",
            started: true,
            status: "completed",
            updatedAt: remote.completedAt || new Date().toISOString(),
          };
          persistStoredState(scopedKey, completed);
          setState(completed);
        } else {
          setState(localState);
          if (localState.status === "completed") {
            void callAuthenticatedWordinariusFunction("completeLearnerGuidedTrial", {});
          }
        }
      } catch {
        if (active) setState(localState);
      } finally {
        if (active) setReady(true);
      }
    };
    void hydrate();
    return () => { active = false; };
  }, [options.accountId, options.enabled, options.syncRemote]);

  useEffect(() => {
    if (!ready || !options.enabled || !options.accountId) return;
    persistStoredState(accountStorageKey(options.accountId), {
      ...state,
      updatedAt: state.updatedAt || new Date().toISOString(),
    });
  }, [options.accountId, options.enabled, ready, state]);

  const update = (next: GuidedTrialState) => {
    setState(next);
    if (options.enabled && options.accountId) {
      persistStoredState(accountStorageKey(options.accountId), next);
    }
  };

  const finish = () => {
    const next = finishGuidedTrial(state);
    update(next);
    if (next.status === "completed" && options.syncRemote) {
      void callAuthenticatedWordinariusFunction("completeLearnerGuidedTrial", {});
    }
  };

  return {
    ready,
    state,
    startPhase: () => update(startGuidedTrialPhase(state)),
    completePhase: () => update(completeGuidedTrialPhase(state)),
    finish,
  };
}

export const GUIDED_TRIAL_LEGACY_STORAGE_KEY = LEGACY_STORAGE_KEY;
export { accountStorageKey as guidedTrialAccountStorageKey };
